import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, Menu, X, FileText } from "lucide-react";
import { COMPANY } from "../lib/data";
import { trackEvent } from "../lib/api";

const NAV = [
  { to: "/", label: "Accueil" },
  { to: "/prestations", label: "Prestations" },
  { to: "/realisations", label: "Réalisations" },
  { to: "/a-propos", label: "À propos" },
  { to: "/zone-intervention", label: "Zone d'intervention" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-xl border-b border-neutral-200 shadow-sm" : "bg-white/60 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10">
        <div className="flex items-center justify-between h-[72px]">
          <Link to="/" data-testid="logo-link" className="flex items-center gap-2.5 group">
            <span className="grid place-items-center h-10 w-10 rounded-md bg-terracotta-500 text-white font-heading font-extrabold text-lg shadow-sm transition-transform group-hover:-translate-y-0.5">C</span>
            <span className="leading-tight">
              <span className="block font-heading font-bold text-[15px] text-neutral-900 tracking-tight">Carnier</span>
              <span className="block text-[11px] uppercase tracking-[0.18em] text-vegetal-500 font-medium">Toiture & Habitat</span>
            </span>
          </Link>

          <nav className="hidden xl:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.to === "/" ? "home" : n.to.replace("/", "")}`}
                className={({ isActive }) =>
                  `px-3 py-2 text-[14px] font-medium rounded-md transition-colors ${
                    isActive ? "text-terracotta-600" : "text-neutral-700 hover:text-terracotta-600"
                  }`
                }
                end={n.to === "/"}
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <a
              href={`tel:${COMPANY.phoneRaw}`}
              data-testid="header-call-button"
              onClick={() => trackEvent("call", "Header - Appeler")}
              className="flex items-center gap-2 px-4 py-2.5 rounded-md border border-vegetal-200 text-vegetal-600 hover:bg-vegetal-50 transition-colors font-medium text-sm"
            >
              <Phone className="h-4 w-4" />
              {COMPANY.phone}
            </a>
            <Link
              to="/devis"
              data-testid="header-devis-button"
              onClick={() => trackEvent("cta", "Header - Demander un devis")}
              className="flex items-center gap-2 px-5 py-2.5 rounded-md bg-terracotta-500 text-white hover:bg-terracotta-600 transition-colors font-medium text-sm shadow-sm"
            >
              <FileText className="h-4 w-4" />
              Demander un devis
            </Link>
          </div>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="xl:hidden md:hidden grid place-items-center h-10 w-10 rounded-md border border-neutral-200 text-neutral-800"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
          <button
            data-testid="mobile-menu-toggle-tablet"
            onClick={() => setOpen((v) => !v)}
            className="hidden md:grid xl:hidden place-items-center h-10 w-10 rounded-md border border-neutral-200 text-neutral-800 ml-2"
            aria-label="Menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="xl:hidden border-t border-neutral-200 bg-white/95 backdrop-blur-xl">
          <div className="max-w-7xl mx-auto px-5 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-md text-[15px] font-medium ${isActive ? "bg-terracotta-50 text-terracotta-600" : "text-neutral-800"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="flex flex-col gap-2 pt-3">
              <a href={`tel:${COMPANY.phoneRaw}`} className="flex items-center justify-center gap-2 px-4 py-3 rounded-md border border-vegetal-200 text-vegetal-600 font-medium">
                <Phone className="h-4 w-4" /> {COMPANY.phone}
              </a>
              <Link to="/devis" className="flex items-center justify-center gap-2 px-4 py-3 rounded-md bg-terracotta-500 text-white font-medium">
                <FileText className="h-4 w-4" /> Demander un devis
              </Link>
              <Link to="/rappel" className="flex items-center justify-center gap-2 px-4 py-3 rounded-md border border-neutral-200 text-neutral-800 font-medium">
                Être rappelé
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
